import { Component, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs/operators';
import { HeaderComponent } from './shared/components/header/header.component';
import { StepNavComponent } from './shared/components/step-nav/step-nav.component';
import { Footer } from './shared/components/footer/footer';
import { WORKFLOW_STEPS } from './shared/workflow-steps';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { LoadingService } from './core/services/loading.service';
import { DataUploadService } from './core/services/data-upload.service';
import { JobService } from './core/services/job.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, HeaderComponent, Footer, StepNavComponent, MatIconModule, MatProgressSpinnerModule],
  templateUrl: './app.html',
  styleUrls: ['./app.scss'],
})
export class App {
  protected title = 'LogMerge';
  protected readonly steps = WORKFLOW_STEPS;
  protected readonly jobs = inject(JobService);
  protected readonly loading = inject(LoadingService);
  private readonly upload = inject(DataUploadService);

  private readonly currentUrl = signal('');

  protected readonly currentIndex = computed(() =>
    this.steps.findIndex((s) => this.currentUrl().startsWith(s.path)),
  );
  protected readonly prevStep = computed(() => this.steps[this.currentIndex() - 1] ?? null);
  protected readonly nextStep = computed(() => this.steps[this.currentIndex() + 1] ?? null);

  /**
   * The Next button is locked until both files are chosen, unless the next
   * step is the Upload step itself.
   */
protected readonly nextLocked = computed(() => {
  const next = this.nextStep();
  return this.jobs.isProcessing() ||
    (!!next && next.path !== '/upload' && !this.upload.ready());
});

  constructor(private readonly router: Router) {
    this.currentUrl.set(this.router.url);
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.currentUrl.set(e.urlAfterRedirects));
  }

  protected goTo(path: string): void {
    this.router.navigateByUrl(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}